import React, { useMemo, useState, useEffect } from "react";
import { Card, Col, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useAppContext } from "providers/AppProvider";
import { InfoCircle, People } from "react-bootstrap-icons";

const AnimatedProgressBar = ({ percentage, isDark, color = '#F43F5E', height = '8px' }) => {
    const [width, setWidth] = useState('0%');

    useEffect(() => {
        const timer = setTimeout(() => {
            setWidth(`${percentage}%`);
        }, 100);
        return () => clearTimeout(timer);
    }, [percentage]);

    return (
        <div className="w-100 rounded-pill" style={{ height: height, backgroundColor: isDark ? '#1e293b' : '#F1F5F9' }}>
            <div
                className="h-100 rounded-pill shadow-sm"
                style={{
                    width: width,
                    backgroundColor: color,
                    transition: 'width 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
            />
        </div>
    );
};

// Component for the Stacked Bar View (All Depts)
const StackedDeptBar = ({ label, data, isDark }) => {
    return (
        <div className="mb-4">
            <div className="d-flex align-items-center mb-2">
                <div className="text-900 fw-bold fs-11" style={{ width: '80px', flexShrink: 0 }}>{label}</div>
                <div className="flex-grow-1 d-flex rounded-pill overflow-hidden" style={{ height: '24px', minWidth: "120px", backgroundColor: isDark ? '#1e293b' : '#F1F5F9' }}>
                    {data.map((segment, idx) => (
                        <OverlayTrigger
                            key={idx}
                            placement="top"
                            overlay={
                                <Tooltip id={`tooltip-${label}-${idx}`} className="fs-11 fw-bold border-0 shadow-lg">
                                    <div className="text-start p-1">
                                        <div className="text-primary-subtle opacity-75 small uppercase ls-1 mb-1">({segment.category})</div>
                                        <div className="mb-1">{segment.description}</div>
                                        <div className="border-top border-white border-opacity-25 pt-1 mt-1 font-sans-serif">
                                            {segment.percentage.toFixed(1)}% ({segment.count} Transaction)
                                        </div>
                                    </div>
                                </Tooltip>
                            }
                        >
                            <div
                                style={{
                                    width: `${segment.percentage}%`,
                                    backgroundColor: segment.color,
                                    transition: 'width 1s ease-in-out',
                                    cursor: 'pointer'
                                }}
                            />
                        </OverlayTrigger>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Main Component
const PreAuthRejectionReason = () => {
    const { generalDashboardData } = useSelector((state) => state.dashboard);
    const { config: { isDark } } = useAppContext();
    const [activeTab, setActiveTab] = useState('general');

    // Selection state for User Drilldown
    const [selectedUser, setSelectedUser] = useState('');

    useEffect(() => {
        if (generalDashboardData?.preauth_rej_user?.length > 0 && !selectedUser) {
            setSelectedUser(generalDashboardData.preauth_rej_user[0].user_name);
        }
    }, [generalDashboardData, selectedUser]);

    // Data selector
    const processedData = useMemo(() => {
        const rejections = generalDashboardData?.preauth_rej || [];
        const sorted = [...rejections]
            .map(item => ({
                code: item.preautherr_code,
                name: item["preautherr_display Approved"] || item.preautherr_display || item.preautherr_code,
                total: Number(item.total || 0)
            }))
            .sort((a, b) => b.total - a.total);

        const grandTotal = sorted.reduce((acc, curr) => acc + curr.total, 0);
        return { sorted, grandTotal };
    }, [generalDashboardData]);

    const deptData = useMemo(() => {
        const depts = generalDashboardData?.preauth_rej_dept || [];
        const top5Categories = processedData.sorted.slice(0, 5).map(c => ({
            code: c.code,
            name: c.name
        }));
        const colors = ['#3b82f6', '#ef4444', '#f59e0b', '#8b5cf6', '#94a3b8'];

        return {
            depts: depts.map(dept => {
                const total = dept.preauth_rej.reduce((acc, r) => acc + Number(r.total), 0);
                return {
                    name: dept.dept_name,
                    segments: top5Categories.map((cat, idx) => {
                        const rej = dept.preauth_rej.find(r => r.preautherr_code === cat.code);
                        const count = Number(rej?.total || 0);
                        return {
                            category: cat.code,
                            description: cat.name,
                            count: count,
                            percentage: total > 0 ? ((count / total) * 100) : 0,
                            color: colors[idx]
                        };
                    })
                };
            }),
            legend: top5Categories.map((cat, idx) => ({
                name: cat.name,
                color: colors[idx]
            }))
        };
    }, [generalDashboardData, processedData]);

    const getBtnClass = (tab) => activeTab === tab
        ? `px-3 py-1 fs-11 fw-bold text-uppercase rounded-2 border-0 shadow-sm transition-all ${isDark ? 'bg-dark text-primary' : ' text-primary'}`
        : `px-3 py-1 fs-11 fw-bold text-uppercase rounded-2 border-0 bg-transparent transition-all ${isDark ? 'text-500 hover-text-200' : 'text-500 hover-text-1000'}`;

    return (
        <Col className="mb-4" md={6} xs={12}>
            <Card className="border-0 h-100 shadow-sm rounded-4">
                <Card.Body className="p-4 d-flex flex-column">

                    {/* Header Section */}
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="bg-danger-subtle p-2 rounded-2 d-flex align-items-center justify-content-center">
                                <InfoCircle className="text-danger" size={20} />
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold fs-9 text-900 ls-n1">
                                    Root Cause Rejection Profiling
                                </h5>
                                <p className="mb-0 text-500 fs-11">
                                    Diagnostic view of top failure reasons across departments
                                </p>
                            </div>
                        </div>

                        <div className={`d-flex p-1 rounded-2 gap-1 border ${isDark ? 'border-700' : 'border-200'}`}>
                            <button className={getBtnClass('general')} onClick={() => setActiveTab('general')}>GENERAL</button>
                            <button className={getBtnClass('all_depts')} onClick={() => setActiveTab('all_depts')}>ALL DEPTS</button>
                            <button className={getBtnClass('users')} onClick={() => setActiveTab('users')}>USER DRILLDOWN</button>
                        </div>
                    </div>

                    <div
                        className="flex-1 custom-scrollbar overflow-x-hidden"
                        style={{ maxHeight: '350px', overflowY: 'auto' }}
                    >
                        {/* GENERAL TAB VIEW */}
                        {activeTab === 'general' && (
                            <div className="d-flex flex-column gap-4 pe-2">
                                {processedData.sorted.slice(0, 15).map((item, index) => {
                                    const percentage = processedData.grandTotal > 0 ? ((item.total / processedData.grandTotal) * 100).toFixed(1) : 0;
                                    return (
                                        <div key={index} className="animate__animated animate__fadeIn">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <div>
                                                    <h6 className="fw-bold text-900 mb-1 fs-11">{index + 1}. {item.name}</h6>
                                                    <span className="text-500 fs-11 fw-bold text-uppercase">{item.total} TRANSACTIONS</span>
                                                </div>
                                                <div className="fw-bolder text-900 fs-9">{percentage}%</div>
                                            </div>
                                            <AnimatedProgressBar percentage={percentage} isDark={isDark} color="#F43F5E" height="6px" />
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* ALL DEPTS TAB VIEW */}
                        {activeTab === 'all_depts' && (
                            <div className="animate__animated animate__fadeIn">
                                <div className="mt-2 pe-2">
                                    {deptData.depts.map((dept, i) => (
                                        <StackedDeptBar key={i} label={dept.name} data={dept.segments} isDark={isDark} />
                                    ))}
                                </div>
                                <div
                                    className={`mt-4 pt-2 border-top border-translucent sticky-bottom-0 pb-2 ${isDark ? 'bg-dark' : 'bg-white'}`}
                                    style={{ position: 'sticky', bottom: 0, zIndex: 5 }}
                                >
                                    <div className="d-flex flex-wrap gap-3">
                                        {deptData.legend.map((item, i) => (
                                            <div key={i} className="d-flex align-items-center gap-2">
                                                <div className="rounded-circle" style={{ width: '10px', height: '10px', backgroundColor: item.color }} />
                                                <span className="text-700 fw-bold fs-11">{item.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* USER DRILLDOWN TAB VIEW */}
                        {activeTab === 'users' && (
                            <div className="animate__animated animate__fadeIn">
                                <div
                                    className={`sticky-top pb-3 z-1 pt-1 mb-3 ${isDark ? 'bg-dark' : 'bg-white'}`}
                                    style={{ position: 'sticky', top: 0, zIndex: 10 }}
                                >
                                    <Form.Group>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <People className="text-primary" size={16} />
                                            <span className="text-900 fw-bold fs-11 text-uppercase">Member Analysis</span>
                                        </div>
                                        <Form.Select
                                            size="sm"
                                            className="border-0 shadow-none fs-11 fw-bold py-2 rounded-3 cursor-pointer"
                                            value={selectedUser}
                                            onChange={(e) => setSelectedUser(e.target.value)}
                                            style={{ backgroundColor: isDark ? '#1e293b' : '#f8fafd' }}
                                        >
                                            {generalDashboardData?.preauth_rej_user?.map((u, i) => (
                                                <option key={i} value={u.user_name}>{u.user_name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                <div className="d-flex flex-column gap-4 pe-2">
                                    {(generalDashboardData?.preauth_rej_user?.find(u => u.user_name === selectedUser)?.preauth_rej || []).map((item, index) => {
                                        const count = Number(item.total);
                                        const userTotal = generalDashboardData?.preauth_rej_user?.find(u => u.user_name === selectedUser)?.preauth_rej.reduce((acc, r) => acc + Number(r.total), 0);
                                        const percentage = userTotal > 0 ? ((count / userTotal) * 100).toFixed(1) : 0;
                                        return (
                                            <div key={index}>
                                                <div className="d-flex justify-content-between align-items-end mb-2">
                                                    <div className="fw-bold text-900 fs-11 text-truncate pe-3">({item.preautherr_code}) {item.preautherr_display}</div>
                                                    <div className="fw-bolder text-900 fs-9">{percentage}%</div>
                                                </div>
                                                <AnimatedProgressBar percentage={percentage} isDark={isDark} color="#3b82f6" height="6px" />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                </Card.Body>
            </Card>

            <style dangerouslySetInnerHTML={{
                __html: `
                .ls-n1 { letter-spacing: -0.01em; }
                .ls-1 { letter-spacing: 0.05em; }
                .bg-soft-danger { background-color: rgba(244, 63, 94, 0.1) !important; }
                .cursor-pointer { cursor: pointer; }
                .border-translucent { border-color: rgba(0, 0, 0, 0.05) !important; }
                .tooltip-inner { max-width: 350px; padding: 0.5rem 0.75rem; border-radius: 0.5rem; background-color: #0b1727; font-family: "Inter", sans-serif; }
                .bs-tooltip-top .tooltip-arrow::before { border-top-color: #0b1727; }
            `}} />
        </Col>
    );
};

export default PreAuthRejectionReason;